import { useMemo } from 'react'

/**
 * Detects the direction of a text string based on the first strongly-typed character.
 * Defaults to 'ltr' if no strong character is found or the string is empty.
 *
 * Note: This is a basic detection and might not be accurate for mixed content
 * or content starting with neutral characters (numbers, punctuation).
 *
 * @param {string | null | undefined} text The text to analyze.
 * @returns {'ltr' | 'rtl'} The detected direction.
 */
function detectTextDirection(text: string | null | undefined): 'ltr' | 'rtl' {
    if (!text || typeof text !== 'string') {
        return 'ltr' // Default to LTR for empty or invalid input
    }

    // Regex for common RTL character ranges (Arabic, Hebrew, etc.)
    const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/
    // Regex for common LTR character ranges (Latin, Cyrillic, Greek, etc.)
    // Basic Latin is often sufficient for a simple check.
    const ltrRegex = /[A-Za-z]/ // Add other LTR ranges if needed

    for (let i = 0; i < text.length; i++) {
        const char = text[i]
        if (rtlRegex.test(char)) {
            return 'rtl'
        }
        if (ltrRegex.test(char)) {
            return 'ltr'
        }
        // Ignore neutral characters (spaces, numbers, punctuation) for this check
    }

    return 'ltr' // Default to LTR if no strong character found
}

/**
 * Custom hook to detect the directionality of a given text string.
 *
 * @param {string | null | undefined} text The text content to analyze.
 * @returns {'ltr' | 'rtl'} The detected direction.
 */
export function useTextDirection(
    text: string | null | undefined,
): 'ltr' | 'rtl' {
    // Default to 'ltr' initially or based on initial detection
    const direction = useMemo(() => detectTextDirection(text), [text])

    return direction
}
